// PushNotificationBanner
// Shows a sticky banner prompting the user to enable push notifications.
// CRITICAL: Notification.requestPermission() MUST be called from a direct user tap.
// This banner ensures that — it never auto-fires the permission prompt.
// Dismissed state is stored in localStorage so it only shows once per device.

import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { needsIOSHomeScreenInstall } from '@/lib/platform';

const DISMISSED_KEY = 'push_banner_dismissed_v1';

export default function PushNotificationBanner() {
  const { isSupported, isSubscribed, isLoading, subscribe } = usePushNotifications();
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash

  useEffect(() => {
    // Check if user already dismissed or subscribed
    const wasDismissed = localStorage.getItem(DISMISSED_KEY) === '1';
    setDismissed(wasDismissed);
  }, []);

  // Don't show if: not supported, already subscribed, or dismissed
  if (!isSupported || isSubscribed || dismissed) return null;

  // iOS Safari tabs need the IOSInstallBanner instead — this generic
  // banner would lie ("Enable" → permission granted but push still
  // doesn't arrive) and clobber the targeted install instructions.
  if (needsIOSHomeScreenInstall()) return null;

  // Also don't show if permission was already granted or denied
  if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return null;
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  const handleEnable = async () => {
    // This tap handler is the user gesture — safe to call requestPermission here
    await subscribe();
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 mx-3 mt-2 rounded-xl"
      style={{
        background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.06))',
        border: '1px solid rgba(245,158,11,0.25)',
      }}
    >
      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
        <Bell className="w-4 h-4 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-black uppercase tracking-wider text-white">Stay in the loop</p>
        <p className="text-[10px] text-white/50 mt-0.5">Enable notifications for updates from Coach Fe</p>
      </div>
      <button
        onClick={handleEnable}
        disabled={isLoading}
        className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-500 text-black text-xs font-black uppercase tracking-wider active:opacity-70 disabled:opacity-50"
      >
        {isLoading ? '...' : 'Enable'}
      </button>
      <button onClick={handleDismiss} className="shrink-0 text-white/30 hover:text-white/60 active:opacity-60 p-1">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
